import { useState, useEffect, useCallback } from 'react';
import {
  institutionWorkflows,
  institutionUsers,
  institutionDepartments,
  institutionRoles,
  institutionConfig,
  type WorkflowTemplate,
  type User,
  type Department,
  type Role,
  type DocumentType,
  type CreateWorkflowStepInput,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import styles from './WorkflowsPage.module.css';

interface StepFormData extends Omit<CreateWorkflowStepInput, 'stepOrder'> {
  tempId: string;
}

export function WorkflowsPage() {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create template modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateDocTypeId, setTemplateDocTypeId] = useState('');
  const [steps, setSteps] = useState<StepFormData[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // View template modal state
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<WorkflowTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await institutionWorkflows.getTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load workflow templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [usersRes, deptsRes, rolesRes, docTypesRes] = await Promise.all([
        institutionUsers.list({ perPage: 100 }),
        institutionDepartments.list(),
        institutionRoles.list(),
        institutionConfig.getDocumentTypes(),
      ]);

      if (usersRes.success && usersRes.data) setUsers(usersRes.data);
      if (deptsRes.success && deptsRes.data) setDepartments(deptsRes.data);
      if (rolesRes.success && rolesRes.data) setRoles(rolesRes.data);
      if (docTypesRes.success && docTypesRes.data) setDocumentTypes(docTypesRes.data);
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchReferenceData();
  }, [fetchTemplates, fetchReferenceData]);

  function openCreateModal() {
    setTemplateName('');
    setTemplateDescription('');
    setTemplateDocTypeId('');
    setSteps([]);
    setError(null);
    setShowCreateModal(true);
  }

  function addStep() {
    setSteps([
      ...steps,
      {
        tempId: crypto.randomUUID(),
        name: '',
        assigneeType: 'user',
        assigneeId: '',
        actionRequired: 'approve',
        dueDays: 3,
        allowDelegate: true,
        allowReject: true,
      },
    ]);
  }

  function updateStep(tempId: string, field: keyof StepFormData, value: unknown) {
    setSteps(steps.map((s) => (s.tempId === tempId ? { ...s, [field]: value } : s)));
  }

  function removeStep(tempId: string) {
    setSteps(steps.filter((s) => s.tempId !== tempId));
  }

  function moveStep(tempId: string, direction: 'up' | 'down') {
    const index = steps.findIndex((s) => s.tempId === tempId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === steps.length - 1)
    ) {
      return;
    }
    const newSteps = [...steps];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[swapIndex]] = [newSteps[swapIndex], newSteps[index]];
    setSteps(newSteps);
  }

  async function handleCreateTemplate() {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }
    if (steps.length === 0) {
      setError('At least one step is required');
      return;
    }
    for (const step of steps) {
      if (!step.name.trim() || !step.assigneeId) {
        setError('All steps must have a name and assignee');
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await institutionWorkflows.createTemplate({
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        documentTypeId: templateDocTypeId || undefined,
        steps: steps.map((s, index) => ({
          stepOrder: index + 1,
          name: s.name.trim(),
          description: s.description,
          assigneeType: s.assigneeType,
          assigneeId: s.assigneeId,
          actionRequired: s.actionRequired,
          dueDays: s.dueDays || undefined,
          allowDelegate: s.allowDelegate,
          allowReject: s.allowReject,
        })),
      });

      if (response.success) {
        setShowCreateModal(false);
        fetchTemplates();
      } else {
        setError(response.error?.message || 'Failed to create template');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  async function openViewModal(template: WorkflowTemplate) {
    setLoadingTemplate(true);
    setShowViewModal(true);
    try {
      const response = await institutionWorkflows.getTemplateById(template.id);
      if (response.success && response.data) {
        setSelectedTemplate(response.data);
      } else {
        setSelectedTemplate(template);
      }
    } catch (err) {
      setSelectedTemplate(template);
    } finally {
      setLoadingTemplate(false);
    }
  }

  function confirmDelete(template: WorkflowTemplate) {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  }

  async function handleDelete() {
    if (!templateToDelete) return;

    setDeleting(true);
    try {
      const response = await institutionWorkflows.deleteTemplate(templateToDelete.id);
      if (response.success) {
        setShowDeleteConfirm(false);
        setTemplateToDelete(null);
        fetchTemplates();
      } else {
        setError(response.error?.message || 'Failed to delete template');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setDeleting(false);
    }
  }

  function getAssigneeLabel(type: string, id: string): string {
    if (type === 'user') {
      const user = users.find((u) => u.id === id);
      return user ? `${user.firstName} ${user.lastName}` : id;
    }
    if (type === 'department') {
      const dept = departments.find((d) => d.id === id);
      return dept ? `${dept.name} (Head)` : id;
    }
    if (type === 'role') {
      const role = roles.find((r) => r.id === id);
      return role ? role.name : id;
    }
    return id;
  }

  function formatAction(action: string): string {
    return action.charAt(0).toUpperCase() + action.slice(1);
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Workflow Templates</h1>
          <p className={styles.subtitle}>Define approval workflows for staff records processing</p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create Template
        </Button>
      </div>

      {error && !showCreateModal && (
        <div className={styles.errorBanner}>{error}</div>
      )}

      <div className={styles.templatesContainer}>
        {loading ? (
          <div className={styles.loading}>Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="2" />
              <path d="M9 12h6" />
              <path d="M9 16h6" />
            </svg>
            <p>No workflow templates defined</p>
            <Button variant="primary" onClick={openCreateModal}>
              Create Your First Template
            </Button>
          </div>
        ) : (
          <div className={styles.templatesList}>
            {templates.map((template) => (
              <div key={template.id} className={styles.templateCard}>
                <div className={styles.templateHeader}>
                  <div className={styles.templateInfo}>
                    <h3 className={styles.templateName}>{template.name}</h3>
                    {template.description && (
                      <p className={styles.templateDesc}>{template.description}</p>
                    )}
                  </div>
                  <span className={`${styles.statusBadge} ${template.isActive ? styles.active : styles.inactive}`}>
                    {template.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className={styles.templateMeta}>
                  <span className={styles.metaItem}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Created {new Date(template.createdAt).toLocaleDateString()}
                  </span>
                </div>

                <div className={styles.templateActions}>
                  <Button variant="secondary" size="sm" onClick={() => openViewModal(template)}>
                    View Steps
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => confirmDelete(template)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Workflow Template"
        size="lg"
      >
        <div className={styles.createModal}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.formSection}>
            <h4 className={styles.sectionTitle}>Template Details</h4>
            <div className={styles.formGrid}>
              <Input
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Leave Request Approval"
                required
              />
              <Select
                label="Record Type (Optional)"
                value={templateDocTypeId}
                onChange={(e) => setTemplateDocTypeId(e.target.value)}
              >
                <option value="">Any record type</option>
                {documentTypes.map((dt) => (
                  <option key={dt.id} value={dt.id}>
                    {dt.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className={styles.formRow}>
              <label className={styles.fieldLabel}>Description (Optional)</label>
              <textarea
                className={styles.textarea}
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Describe when this workflow should be used..."
                rows={2}
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <h4 className={styles.sectionTitle}>Workflow Steps</h4>
              <Button variant="secondary" size="sm" onClick={addStep}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Step
              </Button>
            </div>

            {steps.length === 0 ? (
              <div className={styles.noSteps}>
                <p>No steps added yet. Add at least one step to define the workflow.</p>
              </div>
            ) : (
              <div className={styles.stepsList}>
                {steps.map((step, index) => (
                  <div key={step.tempId} className={styles.stepCard}>
                    <div className={styles.stepNumber}>
                      <span>{index + 1}</span>
                      <div className={styles.stepReorder}>
                        <button
                          type="button"
                          onClick={() => moveStep(step.tempId, 'up')}
                          disabled={index === 0}
                          className={styles.reorderBtn}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="18 15 12 9 6 15" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStep(step.tempId, 'down')}
                          disabled={index === steps.length - 1}
                          className={styles.reorderBtn}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className={styles.stepContent}>
                      <div className={styles.stepRow}>
                        <Input
                          label="Step Name"
                          value={step.name}
                          onChange={(e) => updateStep(step.tempId, 'name', e.target.value)}
                          placeholder="e.g., Supervisor Approval"
                          required
                        />
                        <Select
                          label="Action Required"
                          value={step.actionRequired}
                          onChange={(e) => updateStep(step.tempId, 'actionRequired', e.target.value)}
                        >
                          <option value="approve">Approve</option>
                          <option value="review">Review</option>
                          <option value="sign">Sign</option>
                          <option value="acknowledge">Acknowledge</option>
                        </Select>
                      </div>

                      <div className={styles.stepRow}>
                        <Select
                          label="Assign To"
                          value={step.assigneeType}
                          onChange={(e) => {
                            updateStep(step.tempId, 'assigneeType', e.target.value);
                            updateStep(step.tempId, 'assigneeId', '');
                          }}
                        >
                          <option value="user">Specific User</option>
                          <option value="role">Role</option>
                          <option value="department">Department Head</option>
                        </Select>
                        <Select
                          label={step.assigneeType === 'user' ? 'Select User' : step.assigneeType === 'role' ? 'Select Role' : 'Select Department'}
                          value={step.assigneeId}
                          onChange={(e) => updateStep(step.tempId, 'assigneeId', e.target.value)}
                          required
                        >
                          <option value="">Select...</option>
                          {step.assigneeType === 'user' &&
                            users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.firstName} {u.lastName}
                              </option>
                            ))}
                          {step.assigneeType === 'role' &&
                            roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          {step.assigneeType === 'department' &&
                            departments.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                        </Select>
                      </div>

                      <div className={styles.stepRow}>
                        <Input
                          label="Due Days"
                          type="number"
                          min={1}
                          value={step.dueDays?.toString() || ''}
                          onChange={(e) => updateStep(step.tempId, 'dueDays', parseInt(e.target.value) || null)}
                          placeholder="Optional"
                        />
                        <div className={styles.checkboxGroup}>
                          <label className={styles.checkbox}>
                            <input
                              type="checkbox"
                              checked={step.allowDelegate}
                              onChange={(e) => updateStep(step.tempId, 'allowDelegate', e.target.checked)}
                            />
                            <span>Allow Delegation</span>
                          </label>
                          <label className={styles.checkbox}>
                            <input
                              type="checkbox"
                              checked={step.allowReject}
                              onChange={(e) => updateStep(step.tempId, 'allowReject', e.target.checked)}
                            />
                            <span>Allow Rejection</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className={styles.removeStepBtn}
                      onClick={() => removeStep(step.tempId)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateTemplate} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Template Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedTemplate(null);
        }}
        title={selectedTemplate?.name || 'Workflow Template'}
      >
        <div className={styles.viewModal}>
          {loadingTemplate ? (
            <div className={styles.loading}>Loading template details...</div>
          ) : selectedTemplate ? (
            <>
              {selectedTemplate.description && (
                <p className={styles.viewDescription}>{selectedTemplate.description}</p>
              )}

              <h4 className={styles.stepsTitle}>Workflow Steps</h4>
              {selectedTemplate.steps && selectedTemplate.steps.length > 0 ? (
                <div className={styles.viewStepsList}>
                  {selectedTemplate.steps.map((step, index) => (
                    <div key={step.id} className={styles.viewStep}>
                      <div className={styles.viewStepNumber}>{index + 1}</div>
                      <div className={styles.viewStepContent}>
                        <h5 className={styles.viewStepName}>{step.name}</h5>
                        <div className={styles.viewStepDetails}>
                          <span className={styles.viewStepDetail}>
                            <strong>Action:</strong> {formatAction(step.actionRequired)}
                          </span>
                          <span className={styles.viewStepDetail}>
                            <strong>Assignee:</strong> {getAssigneeLabel(step.assigneeType, step.assigneeId)}
                          </span>
                          {step.dueDays && (
                            <span className={styles.viewStepDetail}>
                              <strong>Due:</strong> {step.dueDays} days
                            </span>
                          )}
                        </div>
                        <div className={styles.viewStepFlags}>
                          {step.allowDelegate && <span className={styles.flag}>Can Delegate</span>}
                          {step.allowReject && <span className={styles.flag}>Can Reject</span>}
                        </div>
                      </div>
                      {index < (selectedTemplate.steps?.length || 0) - 1 && (
                        <div className={styles.stepConnector}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <polyline points="19 12 12 19 5 12" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noStepsView}>No steps defined for this template.</p>
              )}
            </>
          ) : null}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
        }}
        title="Delete Template"
      >
        <div className={styles.deleteModal}>
          <p>
            Are you sure you want to delete <strong>{templateToDelete?.name}</strong>?
          </p>
          <p className={styles.deleteWarning}>
            This action cannot be undone. Templates with active workflow instances cannot be deleted.
          </p>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Template'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
